/**
 * `RealtimeSession.ts` — port of Unity's `RealtimeSession.cs` protocol
 * state machine (trimmed to the web slice).
 *
 * Owns the OpenAI Realtime event protocol over an injected transport:
 * `session.update` (instructions + tools + audio + server VAD),
 * conversation items, response lifecycle, function-call dispatch to the
 * tool executor, and usage parsing. Session hosting/reconnect policy lives
 * in `AIRealtimeSessionModel`; UI state lives behind `AIInterfaceModel`.
 */

import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import type {
  IRealtimeTransport,
  RealtimeMicrophoneReason,
  RealtimeTransportKind,
} from "./IRealtimeTransport";
import type { AgentToolExecutorModel } from "@ai/executor/AgentToolExecutorModel";
import type { IAIAgentToolsModel } from "@ai/registry/AIAgentToolRegistryModel";
import { toolHandedOverSpokenTurn } from "@ai/executor/toolTurnHandover";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import { NarrationResayMatcher } from "./narrationResay";
import {
  getTurnDetectionThreshold,
  getVoiceMicrophoneProfile,
  getVoiceSettings,
} from "@models/data/voiceSettings";

export interface RealtimeSessionOptions {
  readonly instructions: string;
  readonly voiceName: string;
  readonly enableAudioOutput: boolean;
  readonly enableInputAudioTranscription: boolean;
  /**
   * ISO-639-1 code pinned for INPUT TRANSCRIPTION, or "" to let the recognizer
   * pick. Unpinned, `gpt-4o-transcribe` CODE-SWITCHES: "how high is the front
   * cover" came back as "How high ist Frontkopf" (user-reported 2026-09-06) —
   * answered correctly in English, but the user reads the transcript.
   */
  readonly transcriptionLanguage: string;
  /**
   * Whether THIS CLIENT asks the realtime model to answer an accepted audio
   * turn. Server VAD still finds the turn boundary, but response creation and
   * interruption are client-owned so a residual VAD item after one-shot PTT
   * cannot cancel the real answer or manufacture a second one.
   *
   * FALSE while the Responses text bridge answers the active role's turns:
   * the realtime session keeps transcribing (the transcript routes to the
   * bridge) and speaking `INTERNAL SAY:` lines on explicit `response.create`,
   * but must not answer the user itself — that would be two agents replying.
   */
  readonly autoRespondToUserAudio: boolean;
}

/**
 * How the session CONNECTS, resolved from the ACTIVE role by the policy
 * provider (the account's `roleModels` config plus the account-wide
 * `webRtcRealtimeTransport` dev setting; shipped default: `workflow-builder` →
 * gpt-5.6-sol at medium effort, everything else the stock realtime model on
 * the WebSocket).
 *
 * Two of the three members are fixed for a connection's LIFE and one is not,
 * which is the whole reason this type exists as a unit:
 *
 *   - `model` rides the connection URL, so a change RECONNECTS.
 *   - `transport` decides which class is constructed, so a change RECONNECTS.
 *   - `reasoningEffort` rides `session.update`, so a change is just a context
 *     refresh.
 *
 * `AIOrchestratorModel.applySessionModelPolicy` is what applies that split.
 */
export interface RealtimeSessionConnectionConfig {
  readonly model: string;
  /** `null` = omit the `reasoning` key entirely. */
  readonly reasoningEffort: string | null;
  /** Which `IRealtimeTransport` implementation to construct. */
  readonly transport: RealtimeTransportKind;
}

export interface IRealtimeSessionPolicyProvider {
  buildSessionOptions(): RealtimeSessionOptions;
  /** Per-turn "User Context:" message, or null (Unity `BuildTurnContext`). */
  buildTurnUserContext(): string | null;
  /**
   * Model, reasoning effort and transport for the active role — see
   * {@link RealtimeSessionConnectionConfig}.
   *
   * ⚠ **Must stay a pure synchronous read** (map lookups only: no async, no
   * memoisation, no side effects). It runs far more often than it looks —
   * `buildSessionPayloadFrom` calls it on every `sendSessionUpdate` AND every
   * `refreshSessionContext` (once per user turn), and the debug panel's
   * Context tab re-renders it off a 1 s revision tick. An impure version would
   * let the construct-time call and the payload-time call disagree about the
   * transport, so the session host would build one class while the panel
   * reported the other.
   */
  resolveSessionConnection(): RealtimeSessionConnectionConfig;
}

/**
 * Who a piece of assistant text belongs to: `script` is a workflow step's own
 * narration (a `say`), `answer` is everything else.
 */
export type AssistantTextOrigin = "answer" | "script";

/**
 * One piece of assistant text — a delta or a completed part — labelled with
 * the origin of the RESPONSE that produced it (AI-1395).
 *
 * The label travels WITH the text so that every consumer (the panel, which
 * hides narration the play panel already prints, and the saved transcript,
 * which must never record narration as an answer) gets the same answer from
 * the same place. It used to be a live flag each of them read separately,
 * set by the say and cleared by "the next user turn" — and every way of
 * deciding when that turn began was wrong somewhere: a transcript lands
 * ~30 ms AFTER the agent has already acted on the speech it transcribes, a
 * learner who talks over the narration begins a turn in the middle of it, and
 * one who starts speaking in the ~0.5 s between a say and its response
 * cleared the flag before the narration had even started. All three
 * relabelled a step's narration as an answer, which showed it in the chat,
 * re-opened a drawer the step change had just collapsed, and saved it to the
 * transcript as Spark's reply.
 *
 * So the label is decided by CAUSE, not by timing: a say's `response.create`
 * carries {@link K_ORIGIN_METADATA_KEY} in its `metadata`, the server echoes
 * that metadata on the response it creates, and every text event names its
 * `response_id`. Nothing the learner says can change it, and a say whose
 * `response.create` is refused (a response already active) produces no
 * tagged response to mislabel.
 *
 * ⚠ It cannot be derived from the `INTERNAL SAY:` prefix, which is the
 * obvious-looking test: the Responses text bridge delivers its own replies
 * through that same contract, and those ARE answers. The caller has to say.
 *
 * One untagged reply is relabelled `script`: a narration line the model says
 * AGAIN, after "say that again" or after a learner (or a phone's own speaker)
 * talked over it. No request causes that reply, so no tag can mark it; its
 * words are the line's words, which is what `narrationResay.ts` checks. Such a
 * reply's text is held until it is decided, so its first delta already carries
 * the final label (the panel files an entry by its first delta).
 */
export interface AssistantText {
  readonly text: string;
  readonly origin: AssistantTextOrigin;
}

/**
 * One spoken learner turn, keyed by its conversation item: it exists, part of
 * its transcript arrived, or its transcript is final.
 *
 * Web-only. It is what lets a tool call be tied to the words behind it (the
 * step-move guardrail, AI-1412), which the bare-string transcript events cannot
 * do, for two reasons:
 *   - They carry no item id. A turn that exists but has no words yet, or whose
 *     words are coming after the model has already acted, is invisible there.
 *   - {@link RealtimeSession.onUserTranscriptCompleted} drops an EMPTY
 *     transcript. Here it arrives as `""`: a turn of background noise, which is
 *     exactly the turn that must not move the run.
 *
 * `transcript: null` on `settled` means transcription FAILED: the turn happened
 * and nobody will ever know what it said.
 */
export type UserVoiceTurnEvent =
  | { readonly kind: "started"; readonly itemId: string }
  | { readonly kind: "delta"; readonly itemId: string; readonly delta: string }
  | { readonly kind: "settled"; readonly itemId: string; readonly transcript: string | null };

/**
 * The `response.metadata` key a script say's `response.create` carries.
 * Web-only: Unity's `RealtimeSession.cs` sends no metadata.
 */
const K_ORIGIN_METADATA_KEY = "campfire_origin";

/**
 * How many response ids {@link RealtimeSession.m_responseOrigins} remembers.
 * A response's text arrives while it is current or just after, so a short
 * window is plenty; the bound only stops a long session growing the map.
 */
const K_MAX_TRACKED_RESPONSES = 32;
/** Recent item ids retained so late transcript events from rejected audio stay suppressed. */
const K_MAX_TRACKED_USER_AUDIO_ITEMS = 64;

type RealtimeInputMode = "muted" | "one_shot" | "constant";

/**
 * A conversation item id this client assigns, so the item can be deleted
 * later without waiting for the server to name it. The realtime endpoint
 * accepts a client-supplied `item.id` and echoes it on `conversation.item.added`
 * (verified over WebRTC, 2026-10-05); kept short (27 characters) and prefixed
 * so a trace tells it from a server-minted `item_…` id.
 */
function newClientItemId(): string {
  return `cf_${randomUuid().replace(/-/g, "").slice(0, 24)}`;
}

/**
 * One untagged reply's text so far, while it could still be a narration line
 * said again (`narrationResay.ts`). `origin` is null until it is decided, and
 * `held` keeps the deltas not yet emitted.
 */
interface UndecidedReply {
  readonly responseId: string | null;
  text: string;
  held: string[];
  origin: AssistantTextOrigin | null;
}

function responseIdOf(value: unknown): string | null {
  if (typeof value !== "object" || value === null) return null;
  const record = value as Record<string, unknown>;
  const id = record.response_id ?? record.id;
  return typeof id === "string" ? id : null;
}

/** One reply part: the response, the output item and the part within it. */
function replyKey(event: Record<string, unknown>): string {
  const item = typeof event.item_id === "string" ? event.item_id : "";
  const part = typeof event.content_index === "number" ? event.content_index : 0;
  return `${responseIdOf(event) ?? ""}|${item}|${part}`;
}

/**
 * How long to wait for `session.created` before sending the session config
 * regardless. Comfortably past the ~450 ms the campfire proxy takes to dial
 * upstream, and short enough that a proxy which never sends the event costs
 * one turn's worth of delay rather than the session's whole context.
 */
const K_SESSION_READY_FALLBACK_MS = 3000;

interface PendingFunctionCall {
  callId: string;
  name: string;
  argsJson: string;
}

export class RealtimeSession {
  readonly onStatusChanged = new Emitter<string>();
  readonly onResponseDelta = new Emitter<AssistantText>();
  readonly onAssistantTextCompleted = new Emitter<AssistantText>();
  readonly onThinkingStateChanged = new Emitter<boolean>();
  /** Response lifecycle (`response.created` → true, `response.done` → false). */
  readonly onActiveResponseChanged = new Emitter<boolean>();
  readonly onUserVoiceStarted = new Emitter<void>();
  readonly onUserVoiceStopped = new Emitter<void>();
  readonly onUserTranscriptDelta = new Emitter<string>();
  readonly onUserTranscriptCompleted = new Emitter<string>();
  /** Each spoken turn by item id, empty transcripts included — see {@link UserVoiceTurnEvent}. */
  readonly onUserVoiceTurn = new Emitter<UserVoiceTurnEvent>();
  readonly onToolExecutionStarted = new Emitter<{ toolName: string }>();
  readonly onToolExecutionCompleted = new Emitter<{ toolName: string; result: string }>();
  readonly onUsage = new Emitter<{
    model: string;
    inputTokens: number;
    outputTokens: number;
    inputAudioTokens: number;
    outputAudioTokens: number;
    cachedInputTokens: number;
    source: "Realtime" | "Input Transcription";
  }>();
  readonly onError = new Emitter<string>();
  /**
   * Whether the session can carry an outgoing user item RIGHT NOW — see
   * {@link isReadyForUserInput}. Emitted on the transition only.
   */
  readonly onReadyForUserInputChanged = new Emitter<boolean>();

  readonly sessionId = `web-${randomUuid().slice(0, 8)}`;

  private readonly m_transport: IRealtimeTransport;
  private readonly m_toolsModel: IAIAgentToolsModel;
  private readonly m_executor: AgentToolExecutorModel;
  private readonly m_policy: IRealtimeSessionPolicyProvider;
  /** Developer wire log (debug panel's Log tab). Optional — nothing depends on it. */
  private readonly m_log: IRealtimeConversationLog | null;

  /**
   * Whether the SERVER's session exists yet — set by the inbound
   * `session.created`, not by the transport connecting.
   *
   * ⚠ The two are not the same moment, and the gap silently ate the first
   * `session.update` of every session. Measured against the campfire realtime
   * proxy (2026-08-28): the client socket opens ~450 ms BEFORE `session.created`
   * arrives, because the proxy accepts the browser's connection and only then
   * dials upstream — and anything sent in that window is dropped with no
   * `session.updated` and no `error`. Sending after `session.created` was
   * acknowledged every time.
   *
   * So instructions, tool schemas, VAD and transcription config never landed at
   * connect. A TEXT turn self-healed (`sendUserText` refreshes first); a first
   * VOICE turn did not, and ran on server defaults with no tools.
   */
  private m_sessionReady = false;
  /** A `session.update` requested before the server session existed. */
  private m_pendingSessionUpdate = false;
  /**
   * Serialized body of the last `session.update` sent on this connection.
   * `refreshSessionContext` fires before every user turn and on every context
   * change whether or not anything changed, so a byte-identical payload is
   * skipped rather than re-sent (~140 KB) and re-acknowledged. Cleared on
   * disconnect; a refusal that drops a key rebuilds a different body and so
   * is never blocked by this.
   */
  private m_lastSentSessionUpdateJson: string | null = null;
  private m_readyFallbackTimer: number | null = null;

  private m_hasActiveResponse = false;
  private m_isThinking = false;
  private readonly m_pendingCalls = new Map<string, PendingFunctionCall>();
  private m_inFlightToolCount = 0;
  /**
   * At least one function output has been posted for the current tool round,
   * so the model needs exactly one continuation after the originating response
   * is done and every tool in that response has settled.
   */
  private m_toolFollowupPending = false;
  private m_suppressNextFollowup = false;
  /** Origin of each recent response, by id — see {@link AssistantText}. */
  private readonly m_responseOrigins = new Map<string, AssistantTextOrigin>();
  /**
   * Origin of the most recently created response: the fallback for a text
   * event that names no `response_id` the map knows.
   */
  private m_latestResponseOrigin: AssistantTextOrigin = "answer";
  /** The narration lines this conversation has spoken — see {@link AssistantText}. */
  private readonly m_narration = new NarrationResayMatcher();
  /**
   * Untagged replies whose text could still be a narration line said again,
   * by {@link replyKey}. Their deltas are held here until the reply is decided.
   */
  private readonly m_undecidedReplies = new Map<string, UndecidedReply>();
  /** Responses whose untagged text was relabelled `script`, until they end. */
  private readonly m_resaidResponses = new Set<string>();
  /**
   * The items of every user turn this conversation holds but will never answer
   * itself: a routed turn's text (`sendUserText` with `requestResponse: false`),
   * a spoken turn committed while server VAD does not auto-answer, and the
   * `User Context:` items sent with either. The Responses text bridge answers
   * those turns, and with a text-only role its reply never enters this
   * conversation, so each stays here as an open request until
   * {@link discardRoutedTurns} removes it (AI-1460).
   */
  private readonly m_routedTurnItemIds: string[] = [];
  /**
   * Whether this client asks the realtime model to answer an accepted audio
   * turn. The server never auto-answers; bridge-owned roles leave this false.
   */
  private m_shouldRespondToUserAudio = true;
  /** The microphone policy applied before its transport gate changes. */
  private m_inputMode: RealtimeInputMode = "muted";
  /** One one-shot arm authorizes one server-minted audio item. */
  private m_oneShotItemAccepted = false;
  /** Items whose speech_started arrived while the current input mode authorized them. */
  private readonly m_acceptedUserAudioItemIds = new Set<string>();
  /**
   * Items started after one-shot release (or while muted). Kept briefly after
   * deletion so a late transcription completion cannot leak into chat,
   * transcript persistence, workflow guards, or the Responses bridge.
   */
  private readonly m_rejectedUserAudioItemIds = new Set<string>();
  /**
   * `event_id` → item id of each `conversation.item.delete` not yet
   * acknowledged. An `error` naming one is a refused best-effort delete,
   * never a failure the user should hear about.
   */
  private readonly m_pendingDeletes = new Map<string, string>();
  private readonly m_offFns: Array<() => void> = [];
  /**
   * The model this session's socket connected on, pinned at construction for
   * usage attribution: the policy's resolved model can move ahead of the
   * socket (a role switch mid-restart), and tokens must be priced against the
   * model that actually produced them.
   */
  private readonly m_model: string;

  constructor(
    transport: IRealtimeTransport,
    toolsModel: IAIAgentToolsModel,
    executor: AgentToolExecutorModel,
    policy: IRealtimeSessionPolicyProvider,
    log: IRealtimeConversationLog | null = null,
  ) {
    this.m_transport = transport;
    this.m_toolsModel = toolsModel;
    this.m_executor = executor;
    this.m_policy = policy;
    this.m_log = log;
    this.m_model = policy.resolveSessionConnection().model;
    this.m_shouldRespondToUserAudio =
      policy.buildSessionOptions().autoRespondToUserAudio;

    this.m_offFns.push(
      transport.onConnected.on(() => {
        this.onStatusChanged.emit("connected");
        // Requests the update; `session.created` is what releases it.
        this.sendSessionUpdate();
        this.armReadyFallback();
      }),
      transport.onDisconnected.on(() => {
        this.clearReadyFallback();
        const wasReady = this.m_sessionReady;
        this.m_sessionReady = false;
        this.m_pendingSessionUpdate = false;
        this.m_lastSentSessionUpdateJson = null;
        if (wasReady) this.onReadyForUserInputChanged.emit(false);
        // A reply cut off by the drop will never complete; release its text.
        this.settleUndecidedReplies(null);
        this.onStatusChanged.emit("disconnected");
      }),
      transport.onMessage.on((json) => this.handleRealtimeMessage(json)),
      transport.onError.on((code) => this.onError.emit(code)),
    );
    this.m_executor.setSessionId(this.sessionId);
  }

  /**
   * Whether an outgoing user item would actually reach the model.
   *
   * ⚠ STRICTER than "the transport is connected", and the difference is the
   * ~450 ms window {@link m_sessionReady} documents: a `conversation.item.create`
   * sent before `session.created` is dropped by the proxy with no error, and
   * the session config it would be answered under has not been flushed either
   * — so even a line that survived would be answered by a model with no
   * instructions (no `INTERNAL SAY:` contract, no tools).
   *
   * Read by callers that must not lose their message — a workflow script's
   * authored `say` line has no user behind it to notice the silence and retry.
   */
  get isReadyForUserInput(): boolean {
    return this.m_transport.isConnected && this.m_sessionReady;
  }

  get hasActiveResponse(): boolean {
    return this.m_hasActiveResponse;
  }

  get isThinking(): boolean {
    return this.m_isThinking;
  }

  /**
   * Applies the client microphone policy before the transport gate changes.
   *
   * A one-shot arm authorizes exactly one future VAD item. Muting closes the
   * window for NEW starts but does not revoke an already accepted item whose
   * commit/transcript is still in flight. Constant listening authorizes every
   * start until it is disabled.
   */
  setMicrophoneInputState(
    muted: boolean,
    reason: RealtimeMicrophoneReason,
  ): void {
    if (muted) {
      this.m_inputMode = "muted";
      return;
    }
    if (reason === "push_to_talk") {
      this.m_inputMode = "one_shot";
      this.m_oneShotItemAccepted = false;
      return;
    }
    // `constant_listening` is the normal open-mic path. Treat an explicit
    // manual unmute equivalently so this protocol layer never opens a mic and
    // then silently rejects everything it receives.
    this.m_inputMode = "constant";
  }

  shutdown(): void {
    this.clearReadyFallback();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.m_pendingCalls.clear();
    this.m_undecidedReplies.clear();
    this.m_resaidResponses.clear();
    this.m_routedTurnItemIds.length = 0;
    this.m_acceptedUserAudioItemIds.clear();
    this.m_rejectedUserAudioItemIds.clear();
    this.m_pendingDeletes.clear();
  }

  /**
   * Rebuild instructions + tools (Unity `RefreshSessionContext`).
   *
   * Goes through the same readiness gate as the initial update: it fires on
   * every user turn and every role change, so an early role switch would race
   * into exactly the same hole.
   */
  refreshSessionContext(): void {
    if (!this.m_transport.isConnected) return;
    this.sendSessionUpdate();
  }

  suppressNextToolFollowupResponse(): void {
    this.m_suppressNextFollowup = true;
  }

  // -- Outgoing --------------------------------------------------------

  /**
   * `requestResponse: false` posts the item WITHOUT asking the model to answer
   * — used while the text bridge owns the turn, so the realtime conversation
   * keeps the user's words for context but only ever speaks `INTERNAL SAY:`
   * lines. Such a turn is answered elsewhere, so its items are recorded for
   * {@link discardRoutedTurns}.
   *
   * `responseOrigin: "script"` tags the response this asks for as a workflow
   * step's narration — see {@link AssistantText}.
   */
  sendUserText(
    text: string,
    withTurnContext = true,
    requestResponse = true,
    responseOrigin: AssistantTextOrigin = "answer",
  ): void {
    if (!this.m_transport.isConnected) return;
    const routed = !requestResponse;
    if (withTurnContext) this.trySendTurnContext(routed);
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        ...(routed ? { id: this.recordRoutedTurnItem() } : {}),
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
    // The model has now seen this line and can say it again unasked.
    if (responseOrigin === "script") this.m_narration.remember(text);
    if (!requestResponse) return;
    this.sendEvent(
      responseOrigin === "script"
        ? { type: "response.create", response: { metadata: { [K_ORIGIN_METADATA_KEY]: "script" } } }
        : { type: "response.create" },
    );
  }

  /**
   * Adds a line to the conversation for the model to READ — and nothing else:
   * no `response.create`, no turn context, no session refresh. The shape the
   * per-turn `User Context:` item already has, and for the same reasons: the
   * chat renders only what the user typed or said and what the model spoke, so
   * an item posted here never appears there; and it bypasses
   * `AIRealtimeSessionModel.sendUserText`, so it is not counted as a user turn
   * or recorded in the saved transcript.
   *
   * Best-effort: before the server session exists the proxy drops items
   * silently (see {@link m_sessionReady}), and a note is not worth queueing —
   * the next `session.update` carries the same state in its instructions.
   * Returns whether it was sent.
   */
  sendContextNote(text: string): boolean {
    if (!this.isReadyForUserInput) return false;
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
    return true;
  }

  /**
   * `caption` rides the SAME conversation item as the image, as a second
   * content part — never a separate `sendUserText` beforehand.
   *
   * Two reasons it has to be one item. This method fires `response.create`
   * itself, so two calls are two responses, and the second one silently drops
   * (the socket takes one active response at a time). And a caption is the
   * request the picture is FOR: splitting them lets the model answer the image
   * before the question arrives.
   *
   * Absent — which is every caller but the panel's attachment path, the
   * `take_screenshot` executor included — the item is exactly what it was.
   */
  sendUserImage(dataUrl: string, caption?: string): boolean {
    if (!this.m_transport.isConnected) return false;
    const trimmed = caption?.trim() ?? "";
    const content: Array<Record<string, unknown>> = [
      { type: "input_image", image_url: dataUrl },
    ];
    if (trimmed) content.push({ type: "input_text", text: trimmed });
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content,
      },
    });
    this.sendEvent({ type: "response.create" });
    return true;
  }

  forceStopActiveResponse(): boolean {
    if (!this.m_transport.isConnected) return false;
    if (this.m_hasActiveResponse) {
      this.sendEvent({ type: "response.cancel" });
    }
    this.m_transport.stopOutputAudioPlayback();
    this.setActiveResponse(false);
    this.setThinking(false);
    return true;
  }

  /**
   * Deletes the items of every turn this conversation never answered itself —
   * see {@link m_routedTurnItemIds} — and returns how many it asked to delete.
   *
   * Called when the conversation those turns belonged to ends: the bridge
   * resetting on a role switch, a project switch or a conversation reset. They
   * cannot be left behind, because the realtime model treats an unanswered
   * user item as a request still waiting, and the NEXT response it is asked
   * for answers it. Pressing Play after a Workflow Builder request opened the
   * run with the Guide refusing that request instead of narrating the Welcome,
   * three runs out of three (AI-1460). An answered turn is left alone: it asks
   * nothing.
   *
   * Best-effort. A refusal (an item the server never received — sent in the
   * gap before `session.created` — or already gone) is swallowed by its
   * `event_id`, so it never reaches the panel or the model-policy self-heal.
   */
  discardRoutedTurns(): number {
    const itemIds = this.m_routedTurnItemIds.splice(0);
    if (itemIds.length === 0 || !this.m_transport.isConnected) return 0;
    for (const itemId of itemIds) {
      this.deleteConversationItem(itemId);
    }
    this.m_log?.note("routed_turns.discarded", `${itemIds.length} item(s)`);
    return itemIds.length;
  }

  /** Mints the id of a routed turn's item and records it — see {@link m_routedTurnItemIds}. */
  private recordRoutedTurnItem(): string {
    const itemId = newClientItemId();
    this.m_routedTurnItemIds.push(itemId);
    return itemId;
  }

  /** `routed`: the turn this context precedes is answered elsewhere, so the item is recorded too. */
  private trySendTurnContext(routed = false): void {
    const userContext = this.m_policy.buildTurnUserContext();
    if (!userContext) return;
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        ...(routed ? { id: this.recordRoutedTurnItem() } : {}),
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: `User Context:\n${userContext}` }],
      },
    });
  }

  private rememberUserAudioItem(items: Set<string>, itemId: string): void {
    items.delete(itemId);
    items.add(itemId);
    while (items.size > K_MAX_TRACKED_USER_AUDIO_ITEMS) {
      const oldest = items.values().next().value as string | undefined;
      if (!oldest) break;
      items.delete(oldest);
    }
  }

  /**
   * Latches a VAD item to the input mode that was live when speech began.
   * Later mute/release cannot revoke it; later starts cannot borrow its PTT.
   */
  private acceptUserAudioStart(itemId: string): boolean {
    if (this.m_acceptedUserAudioItemIds.has(itemId)) return true;
    if (this.m_rejectedUserAudioItemIds.has(itemId)) return false;

    const accepted =
      this.m_inputMode === "constant"
      || (this.m_inputMode === "one_shot" && !this.m_oneShotItemAccepted);
    if (accepted) {
      if (this.m_inputMode === "one_shot") this.m_oneShotItemAccepted = true;
      this.rememberUserAudioItem(this.m_acceptedUserAudioItemIds, itemId);
      return true;
    }

    this.rememberUserAudioItem(this.m_rejectedUserAudioItemIds, itemId);
    this.m_log?.note(
      "user_audio.rejected",
      `item=${itemId}, mode=${this.m_inputMode}`,
      { item_id: itemId, input_mode: this.m_inputMode },
    );
    return false;
  }

  private isAcceptedUserAudioItem(itemId: string): boolean {
    if (this.m_acceptedUserAudioItemIds.has(itemId)) return true;
    if (this.m_rejectedUserAudioItemIds.has(itemId)) return false;
    // Defensive fallback for a provider/proxy that drops speech_started but
    // still commits. Apply the live gate rather than accepting while muted.
    return this.acceptUserAudioStart(itemId);
  }

  private deleteConversationItem(itemId: string): void {
    if (!itemId || !this.m_transport.isConnected) return;
    const eventId = `cf-del-${randomUuid().slice(0, 8)}`;
    this.m_pendingDeletes.set(eventId, itemId);
    this.sendEvent({
      type: "conversation.item.delete",
      event_id: eventId,
      item_id: itemId,
    });
  }

  /**
   * Sends the session config, or COALESCES it until the server session exists.
   *
   * Coalesced rather than queued: the payload is rebuilt from live state at
   * flush time, so several requests before readiness are one update carrying
   * the newest context — never a burst of ~30 KB updates.
   */
  private sendSessionUpdate(): void {
    if (!this.m_sessionReady) {
      this.m_pendingSessionUpdate = true;
      return;
    }
    this.sendSessionUpdateNow();
  }

  /**
   * Builds and sends the session config, unless it is byte-identical to the
   * one already on the wire for this connection (see
   * {@link m_lastSentSessionUpdateJson}). Compared on the serialized body so
   * `buildSessionPayload` stays the pure read its contract requires.
   */
  private sendSessionUpdateNow(): void {
    const options = this.m_policy.buildSessionOptions();
    const session = RealtimeSession.buildSessionPayloadFrom(
      this.m_policy,
      this.m_toolsModel,
      options,
    );
    const sessionJson = JSON.stringify(session);
    // This client-only policy can change even when the wire payload does not:
    // server create_response is always false now.
    this.m_shouldRespondToUserAudio = options.autoRespondToUserAudio;
    if (sessionJson === this.m_lastSentSessionUpdateJson) {
      this.m_log?.note("session.update.skipped", "payload unchanged; not re-sent");
      return;
    }
    this.m_lastSentSessionUpdateJson = sessionJson;
    this.sendEvent({ type: "session.update", session });
  }

  /**
   * Marks the server session live and flushes anything held for it.
   *
   * ⚠ Also reached by {@link armReadyFallback}, so a transport or proxy that
   * never forwards `session.created` degrades to the OLD behaviour rather than
   * leaving the session's context unset forever. Without that, this fix would
   * trade a probabilistic failure for a deterministic one.
   */
  private markSessionReady(): void {
    this.clearReadyFallback();
    if (this.m_sessionReady) return;
    this.m_sessionReady = true;
    if (this.m_pendingSessionUpdate) {
      this.m_pendingSessionUpdate = false;
      this.sendSessionUpdateNow();
    }
    // AFTER the config flush, never before: a waiter released here sends its
    // item on the next line, and the two ride one ordered connection — so the
    // instructions have to be on the wire first or the line is answered
    // without them.
    if (this.m_transport.isConnected) this.onReadyForUserInputChanged.emit(true);
  }

  private armReadyFallback(): void {
    this.clearReadyFallback();
    this.m_readyFallbackTimer = window.setTimeout(() => {
      this.m_readyFallbackTimer = null;
      if (this.m_sessionReady) return;
      console.warn(
        "[RealtimeSession] no session.created within " +
          `${K_SESSION_READY_FALLBACK_MS}ms — sending the session config anyway`,
      );
      this.m_log?.note("session.ready-fallback", `${K_SESSION_READY_FALLBACK_MS}ms`);
      this.markSessionReady();
    }, K_SESSION_READY_FALLBACK_MS);
  }

  private clearReadyFallback(): void {
    if (this.m_readyFallbackTimer === null) return;
    window.clearTimeout(this.m_readyFallbackTimer);
    this.m_readyFallbackTimer = null;
  }

  /**
   * The `session.update` body as it would be sent RIGHT NOW — instructions,
   * the enabled tool schemas for the active role/skills, and the audio config.
   *
   * Public and side-effect-free so the debug panel's "Context" tab can
   * render the live payload rather than a stale copy captured at connect time:
   * the instructions carry the project/scene context and the tool list is
   * role-gated, so both change while the session is up. The connected session
   * is not consulted at all, which is also why this answers before a session
   * exists.
   */
  buildSessionPayload(): Record<string, unknown> {
    return RealtimeSession.buildSessionPayloadFrom(this.m_policy, this.m_toolsModel);
  }

  /**
   * The same payload without an instance. `AIRealtimeSessionModel` uses it to
   * answer the debug panel while Spark is offline, when there is no session to
   * ask — everything the body is made of comes from the policy and the tools
   * model, neither of which needs a connection.
   */
  static buildSessionPayloadFrom(
    policy: IRealtimeSessionPolicyProvider,
    toolsModel: IAIAgentToolsModel,
    options: RealtimeSessionOptions = policy.buildSessionOptions(),
  ): Record<string, unknown> {
    const voiceSettings = getVoiceSettings();
    const deviceProfile = getVoiceMicrophoneProfile();
    const modelConfig = policy.resolveSessionConnection();
    const session: Record<string, unknown> = {
      type: "realtime",
      instructions: options.instructions,
      parallel_tool_calls: voiceSettings.realtime.parallelToolCalls,
      // Only present when the active role configures one (`roleModels`):
      // the stock realtime path keeps its wire shape byte-identical.
      ...(modelConfig.reasoningEffort !== null
        ? { reasoning: { effort: modelConfig.reasoningEffort } }
        : {}),
      output_modalities: options.enableAudioOutput ? ["audio"] : ["text"],
      tools: toolsModel.getEnabledToolSchemas(),
      audio: {
        input: {
          format: { type: "audio/pcm", rate: 24000 },
          // App Settings selects the per-browser profile. Ordinary browsers
          // default to far-field; headset browsers default to near-field.
          // The preference is sampled when this payload is built and does not
          // mutate an already-running Spark session. The filter runs before
          // VAD/transcription.
          noise_reduction: { type: deviceProfile.noiseReduction },
          turn_detection: {
            type: "server_vad",
            create_response: options.autoRespondToUserAudio,
            interrupt_response: voiceSettings.turnDetection.interruptResponse,
            threshold: getTurnDetectionThreshold(),
            prefix_padding_ms: voiceSettings.turnDetection.prefixPaddingMs,
            silence_duration_ms: voiceSettings.turnDetection.silenceDurationMs,
            idle_timeout_ms: voiceSettings.turnDetection.idleTimeoutMs,
          },
          // The language pin is OMITTED when empty rather than sent blank: an
          // unknown or malformed key here is refused at `session.update`, which
          // voids the WHOLE update so instructions and tool schemas never land
          // either. That is also what the orchestrator's transcription
          // self-heal clears it for.
          //
          // ⚠ NOTHING PROJECT-DERIVED MAY BE ADDED HERE. A `prompt` carrying the
          // scene's proper nouns shipped 2026-09-06 and was reverted the same
          // day: on a near-silent buffer `gpt-4o-transcribe` CONTINUED it and
          // returned the continuation as a completed user transcript, which
          // renders as the user speaking, wipes the agent's on-screen
          // measurement, writes to the durable transcript and can release a
          // workflow `wait_for`. `prompt` is documented for describing the
          // recording's setting, not for vocabulary; the purpose-built field is
          // `keywords` ("Keywords are hints, not required output"), which this
          // endpoint's model does not take. Measure that on a throwaway session
          // before reaching for it — see the ⚠ on the self-heal latch.
          ...(options.enableInputAudioTranscription
            ? {
                transcription: {
                  model: voiceSettings.transcription.model,
                  ...(options.transcriptionLanguage
                    ? { language: options.transcriptionLanguage }
                    : {}),
                },
              }
            : {}),
        },
        output: {
          format: { type: "audio/pcm", rate: 24000 },
          voice: options.voiceName,
        },
      },
    };
    return session;
  }

  private sendEvent(event: Record<string, unknown>): void {
    if (import.meta.env.DEV) {
      console.debug(`[RealtimeSession] → ${String(event.type)}`);
    }
    this.m_log?.logOutbound(event);
    this.m_transport.sendEvent(JSON.stringify(event));
  }

  // -- Incoming --------------------------------------------------------

  private handleRealtimeMessage(json: string): void {
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(json) as Record<string, unknown>;
    } catch {
      return;
    }
    const type = typeof event.type === "string" ? event.type : "";
    this.m_log?.logInbound(event);

    switch (type) {
      case "session.created":
        // The moment the server session exists — see `m_sessionReady`.
        this.markSessionReady();
        break;
      case "session.updated":
        break;
      case "response.created":
        // Before any of the response's text can arrive — see `AssistantText`.
        this.noteResponseOrigin(event.response);
        this.setActiveResponse(true);
        this.setThinking(true);
        // Restores Unity's `IRealtimeTransport` contract ("called when session
        // detects new audio output starting"), which the web port had dropped.
        // On the WebSocket transport this is a harmless `context.resume()`; on
        // WebRTC it is the DETERMINISTIC un-silence after a barge-in, without
        // which one Stop press deafens Spark for the rest of the session.
        this.m_transport.resumeAudioOutputIfNeeded();
        break;
      case "response.output_text.delta":
      case "response.output_audio_transcript.delta": {
        // Guarded for the same reason as `response.done`: a model that speaks
        // before it calls a tool would otherwise clear the flag for the round.
        this.clearThinkingUnlessToolsInFlight();
        const delta = typeof event.delta === "string" ? event.delta : "";
        if (delta) this.emitResponseDelta(event, delta);
        break;
      }
      case "response.output_text.done":
      case "response.output_audio_transcript.done": {
        const text = typeof event.text === "string" ? event.text : (event.transcript as string) ?? "";
        if (text) this.emitAssistantTextCompleted(event, text);
        break;
      }
      case "response.done": {
        const responseId = responseIdOf(event.response);
        // A reply with no `.done` of its own (none observed, but the protocol
        // does not promise one) must not keep its text held past its response.
        this.settleUndecidedReplies(responseId);
        this.noteResponseEnded(event.response, responseId);
        this.setActiveResponse(false);
        // ⚠ NOT an unconditional clear: a response carrying a function call is
        // DONE the moment it has emitted the call, which is long before the
        // tool it asked for has run. See `clearThinkingUnlessToolsInFlight`.
        this.clearThinkingUnlessToolsInFlight();
        this.parseResponseUsage(event);
        this.trySendToolFollowup();
        break;
      }
      case "response.output_item.added":
        this.handleOutputItemAdded(event);
        break;
      case "response.function_call_arguments.done":
        this.handleFunctionArgsDone(event);
        break;
      case "input_audio_buffer.speech_started": {
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        if (itemId && !this.acceptUserAudioStart(itemId)) break;
        if (!itemId && this.m_inputMode === "muted") break;
        // Provider-owned interruption is disabled in the session payload.
        // Interrupt only a turn authorized by the live microphone mode, so a
        // residual VAD item cannot cancel the answer to the real one.
        if (this.m_hasActiveResponse || this.m_transport.isAgentSpeaking) {
          this.forceStopActiveResponse();
        }
        this.onUserVoiceStarted.emit();
        this.trySendTurnContext(!this.m_shouldRespondToUserAudio);
        break;
      }
      case "input_audio_buffer.speech_stopped": {
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        if (itemId && !this.isAcceptedUserAudioItem(itemId)) break;
        if (!itemId && this.m_inputMode === "muted") break;
        this.onUserVoiceStopped.emit();
        break;
      }
      // The mic's audio became a turn. Its item id is what the transcript
      // events below name.
      case "input_audio_buffer.committed": {
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        if (!itemId) break;
        if (!this.isAcceptedUserAudioItem(itemId)) {
          this.deleteConversationItem(itemId);
          this.m_log?.note("user_audio.rejected-committed", `item=${itemId}`);
          break;
        }
        if (this.m_shouldRespondToUserAudio) {
          this.sendEvent({ type: "response.create" });
        } else {
          // The transcript is routed to the bridge, and this realtime
          // conversation never answers the turn — see `m_routedTurnItemIds`.
          this.m_routedTurnItemIds.push(itemId);
        }
        this.onUserVoiceTurn.emit({ kind: "started", itemId });
        break;
      }
      case "conversation.item.deleted": {
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        for (const [eventId, pendingItemId] of this.m_pendingDeletes) {
          if (pendingItemId === itemId) this.m_pendingDeletes.delete(eventId);
        }
        break;
      }
      // Audio a client posted as an item (the `evals/` drivers do) never passes
      // the input buffer, so this is its only start. A mic turn arrives here
      // too; the consumer dedupes by id. Both names: `created` is the beta's,
      // `added` the GA protocol's.
      case "conversation.item.added":
      case "conversation.item.created": {
        const itemId = userAudioItemId(event.item);
        if (
          itemId
          && !this.m_rejectedUserAudioItemIds.has(itemId)
        ) {
          this.onUserVoiceTurn.emit({ kind: "started", itemId });
        }
        break;
      }
      case "conversation.item.input_audio_transcription.delta": {
        const delta = typeof event.delta === "string" ? event.delta : "";
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        if (itemId && this.m_rejectedUserAudioItemIds.has(itemId)) break;
        if (delta) this.onUserTranscriptDelta.emit(delta);
        if (delta && itemId) this.onUserVoiceTurn.emit({ kind: "delta", itemId, delta });
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const transcript = typeof event.transcript === "string" ? event.transcript : "";
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        this.parseTranscriptionUsage(event);
        if (itemId && this.m_rejectedUserAudioItemIds.has(itemId)) {
          this.m_log?.note("user_audio.rejected-transcript", `item=${itemId}`);
          break;
        }
        if (transcript) this.onUserTranscriptCompleted.emit(transcript);
        if (itemId) this.onUserVoiceTurn.emit({ kind: "settled", itemId, transcript });
        break;
      }
      case "conversation.item.input_audio_transcription.failed": {
        const itemId = typeof event.item_id === "string" ? event.item_id : "";
        if (itemId && this.m_rejectedUserAudioItemIds.has(itemId)) break;
        if (itemId) this.onUserVoiceTurn.emit({ kind: "settled", itemId, transcript: null });
        break;
      }
      case "error": {
        const error = event.error as Record<string, unknown> | undefined;
        const code = typeof error?.code === "string" ? error.code : "realtime_error";
        if (import.meta.env.DEV) {
          console.warn("[RealtimeSession] server error event:", JSON.stringify(error ?? event));
        }
        // A refused best-effort item delete — routed or rejected audio.
        const eventId = typeof error?.event_id === "string" ? error.event_id : "";
        if (eventId && this.m_pendingDeletes.delete(eventId)) {
          this.m_log?.note("conversation_item.delete-refused", code);
          break;
        }
        // Benign races Unity suppresses too.
        if (code !== "response_cancel_not_active" && code !== "conversation_already_has_active_response") {
          this.onError.emit(code);
        }
        break;
      }
      default:
        break;
    }
  }

  /** Records a new response's origin from the metadata it echoes. */
  private noteResponseOrigin(response: unknown): void {
    const r = response as { id?: unknown; metadata?: Record<string, unknown> | null } | undefined;
    const origin: AssistantTextOrigin =
      r?.metadata?.[K_ORIGIN_METADATA_KEY] === "script" ? "script" : "answer";
    this.m_latestResponseOrigin = origin;
    if (typeof r?.id !== "string") return;
    this.m_responseOrigins.set(r.id, origin);
    if (this.m_responseOrigins.size > K_MAX_TRACKED_RESPONSES) {
      const oldest = this.m_responseOrigins.keys().next().value;
      if (oldest !== undefined) this.m_responseOrigins.delete(oldest);
    }
  }

  /** The origin of the response a text event belongs to. */
  private originOf(event: Record<string, unknown>): AssistantTextOrigin {
    const id = event.response_id;
    return (typeof id === "string" ? this.m_responseOrigins.get(id) : undefined)
      ?? this.m_latestResponseOrigin;
  }

  /**
   * Emits one delta of reply text with its label. A tagged narration passes
   * straight through, and so does every reply while no narration has been
   * spoken. An untagged reply is held while its words could still be a
   * narration line said again, and released, labelled, the moment that is
   * decided.
   *
   * The hold costs an answer nothing unless it opens with a narration's own
   * words, and then only until the first word that differs.
   */
  private emitResponseDelta(event: Record<string, unknown>, delta: string): void {
    const origin = this.originOf(event);
    if (origin === "script") {
      this.onResponseDelta.emit({ text: delta, origin });
      return;
    }
    // Every untagged reply gets an entry, even one decided at its first delta,
    // so its completion reuses that decision: a narration spoken MID-reply
    // must not relabel text that already went out as an answer.
    const key = replyKey(event);
    let reply = this.m_undecidedReplies.get(key);
    if (!reply) {
      reply = { responseId: responseIdOf(event), text: "", held: [], origin: null };
      this.m_undecidedReplies.set(key, reply);
    }
    reply.text += delta;
    if (reply.origin !== null) {
      this.onResponseDelta.emit({ text: delta, origin: reply.origin });
      return;
    }
    reply.held.push(delta);
    if (this.m_narration.classify(reply.text, false) === "answer") {
      this.decideReply(reply, "answer");
    }
  }

  /** Emits a completed reply part, deciding a held reply on its final text. */
  private emitAssistantTextCompleted(event: Record<string, unknown>, text: string): void {
    let origin = this.originOf(event);
    if (origin !== "script") {
      const key = replyKey(event);
      const reply: UndecidedReply = this.m_undecidedReplies.get(key)
        ?? { responseId: responseIdOf(event), text, held: [], origin: null };
      this.m_undecidedReplies.delete(key);
      origin = this.finishReply(reply, text);
    }
    this.onAssistantTextCompleted.emit({ text, origin });
  }

  /** Labels a held reply and releases the deltas it was holding. */
  private decideReply(reply: UndecidedReply, origin: AssistantTextOrigin): void {
    reply.origin = origin;
    const held = reply.held;
    reply.held = [];
    for (const text of held) this.onResponseDelta.emit({ text, origin });
  }

  /**
   * Decides an untagged reply on its complete text, unless its deltas already
   * decided it, and records what that means for the next reply: a cut-off
   * explains only the reply right after it, and a reply that said a line again
   * can itself be cut off.
   */
  private finishReply(reply: UndecidedReply, finalText: string): AssistantTextOrigin {
    const origin = reply.origin
      ?? (this.m_narration.classify(finalText, true) === "resay" ? "script" : "answer");
    if (reply.origin === null) this.decideReply(reply, origin);
    this.m_narration.noteReplySettled();
    if (origin === "script" && reply.responseId !== null) this.m_resaidResponses.add(reply.responseId);
    return origin;
  }

  /**
   * Decides every reply still held for `responseId` (`null` = all of them) on
   * the text it has, and forgets them. For a reply whose own `.done` never
   * came: its response ended, or the connection did.
   */
  private settleUndecidedReplies(responseId: string | null): void {
    for (const [key, reply] of this.m_undecidedReplies) {
      if (responseId !== null && reply.responseId !== responseId) continue;
      this.m_undecidedReplies.delete(key);
      this.finishReply(reply, reply.text);
    }
  }

  /**
   * A cancelled response that was speaking a narration line, tagged or said
   * again, cut that line off — see `narrationResay.ts`. A server-VAD barge-in
   * and the Stop button both end a response this way.
   */
  private noteResponseEnded(response: unknown, responseId: string | null): void {
    if (responseId === null) return;
    const narrated = this.m_responseOrigins.get(responseId) === "script"
      || this.m_resaidResponses.has(responseId);
    this.m_resaidResponses.delete(responseId);
    const status = (response as { status?: unknown } | undefined)?.status;
    if (narrated && status === "cancelled") this.m_narration.noteCutOff();
  }

  private handleOutputItemAdded(event: Record<string, unknown>): void {
    const item = event.item as Record<string, unknown> | undefined;
    if (!item || item.type !== "function_call") return;
    const callId = typeof item.call_id === "string" ? item.call_id : "";
    const name = typeof item.name === "string" ? item.name : "";
    if (!callId || !name) return;
    this.m_pendingCalls.set(callId, {
      callId,
      name,
      argsJson: typeof item.arguments === "string" ? item.arguments : "",
    });
    // Inline args (rare) execute immediately; streamed args wait for done.
    if (this.m_pendingCalls.get(callId)!.argsJson) {
      void this.executePendingCall(callId);
    }
  }

  private handleFunctionArgsDone(event: Record<string, unknown>): void {
    const callId = typeof event.call_id === "string" ? event.call_id : "";
    if (!callId) return;
    const argsJson = typeof event.arguments === "string" ? event.arguments : "";
    const pending = this.m_pendingCalls.get(callId);
    if (pending) {
      pending.argsJson = argsJson;
    } else {
      const name = typeof event.name === "string" ? event.name : "";
      if (!name) return;
      this.m_pendingCalls.set(callId, { callId, name, argsJson });
    }
    void this.executePendingCall(callId);
  }

  private async executePendingCall(callId: string): Promise<void> {
    const pending = this.m_pendingCalls.get(callId);
    if (!pending) return;
    this.m_pendingCalls.delete(callId);

    let args: Record<string, unknown> = {};
    if (pending.argsJson) {
      try {
        args = JSON.parse(pending.argsJson) as Record<string, unknown>;
      } catch {
        args = {};
      }
    }

    this.m_inFlightToolCount++;
    this.setThinking(true);
    this.onToolExecutionStarted.emit({ toolName: pending.name });
    // Logged with the resolved ARGS, not the raw fragment stream: the debug
    // Log tab wants what the tool was actually called with.
    this.m_log?.logTool(pending.name, "call", args);

    let result: string;
    try {
      result = await this.m_executor.executeAsync(pending.name, args, "AI");
    } catch (err) {
      result = `error: ${err instanceof Error ? err.message : String(err)}`;
    }
    this.m_inFlightToolCount--;
    this.onToolExecutionCompleted.emit({ toolName: pending.name, result });
    this.m_log?.logTool(pending.name, "result", result);

    // Some tools hand the SPOKEN turn away — the learner answers a
    // `present_options` round with a button, and a workflow step the run just
    // moved to narrates itself. Asking for a follow-up reply there either talks
    // over whoever is speaking or, for a step arrival, gets composed from the
    // NEW step's freshly-pushed run-state bullets. `toolTurnHandover.ts` owns
    // the rule; the Responses bridge reads the same one.
    if (toolHandedOverSpokenTurn(pending.name, result)) {
      this.m_suppressNextFollowup = true;
    }

    this.sendEvent({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: result },
    });
    this.m_toolFollowupPending = true;
    this.trySendToolFollowup();
  }

  /**
   * Continue a tool round only after BOTH boundaries have closed:
   *
   * 1. every tool emitted by the response has finished; and
   * 2. the response itself has emitted `response.done`.
   *
   * The second boundary is load-bearing for parallel calls. A fast first tool
   * can finish before the server has even announced the response's second
   * function call. Continuing at that instant starts an answer over the slow
   * tool and makes its eventual `response.create` fail with
   * `conversation_already_has_active_response`.
   */
  private trySendToolFollowup(): void {
    if (
      !this.m_toolFollowupPending
      || this.m_hasActiveResponse
      || this.m_inFlightToolCount > 0
      || this.m_pendingCalls.size > 0
    ) {
      return;
    }

    this.m_toolFollowupPending = false;
    if (this.m_suppressNextFollowup) {
      this.m_suppressNextFollowup = false;
      this.setThinking(false);
    } else {
      this.sendEvent({ type: "response.create" });
    }
  }

  /**
   * End the thinking state ONLY if no tool is still running.
   *
   * ⚠ A response that carries a function call reaches `response.done` as soon
   * as it has emitted that call — the model's turn is over, but the WORK it
   * asked for has not started. So an unconditional clear there switches the
   * activity visualizer off for the whole tool round, and the slower the tool
   * the worse it is: a fast tool re-arms the flag within a frame or two when
   * the follow-up `response.created` lands, which is why this was invisible
   * until `Prompt_Search_Docs` — a gpt-5.6 Responses call that takes SECONDS —
   * left the pill dark for the entire search (user-reported, 2026-09-04).
   *
   * This does NOT risk a stuck flag. `m_inFlightToolCount` is decremented in
   * the dispatch's own tail (after a try/catch, so a throwing tool still
   * counts down), and that tail then either sends the follow-up `response.create`
   * — whose eventual `response.done` clears with the count back at 0 — or, for
   * a handover, clears explicitly. `forceStopActiveResponse` deliberately keeps
   * its UNCONDITIONAL `setThinking(false)`: the user pressed Stop, so
   * everything stops whatever is in flight.
   */
  private clearThinkingUnlessToolsInFlight(): void {
    if (this.m_inFlightToolCount > 0) return;
    this.setThinking(false);
  }

  private setThinking(thinking: boolean): void {
    if (this.m_isThinking === thinking) return;
    this.m_isThinking = thinking;
    this.onThinkingStateChanged.emit(thinking);
  }

  private setActiveResponse(active: boolean): void {
    if (this.m_hasActiveResponse === active) return;
    this.m_hasActiveResponse = active;
    this.onActiveResponseChanged.emit(active);
  }

  private parseResponseUsage(event: Record<string, unknown>): void {
    const response = event.response as Record<string, unknown> | undefined;
    const usage = response?.usage as Record<string, unknown> | undefined;
    if (!usage) return;
    const details = (usage.input_token_details ?? {}) as Record<string, unknown>;
    const outDetails = (usage.output_token_details ?? {}) as Record<string, unknown>;
    const cached = (details.cached_tokens_details ?? {}) as Record<string, unknown>;
    this.onUsage.emit({
      model: this.m_model,
      inputTokens: num(details.text_tokens) || num(usage.input_tokens),
      outputTokens: num(outDetails.text_tokens) || num(usage.output_tokens),
      inputAudioTokens: num(details.audio_tokens),
      outputAudioTokens: num(outDetails.audio_tokens),
      cachedInputTokens: num(details.cached_tokens) || num(cached.text_tokens),
      source: "Realtime",
    });
  }

  private parseTranscriptionUsage(event: Record<string, unknown>): void {
    const usage = event.usage as Record<string, unknown> | undefined;
    if (!usage) return;
    const details = (usage.input_token_details ?? {}) as Record<string, unknown>;
    this.onUsage.emit({
      model: getVoiceSettings().transcription.model,
      inputTokens: num(usage.input_tokens),
      outputTokens: num(usage.output_tokens),
      inputAudioTokens: num(details.audio_tokens),
      outputAudioTokens: 0,
      cachedInputTokens: 0,
      source: "Input Transcription",
    });
  }
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/**
 * The id of a conversation item the USER spoke — a user message carrying
 * audio — or null for anything else (typed text, a context note, the model's
 * own items, a tool call).
 */
function userAudioItemId(item: unknown): string | null {
  const i = item as { id?: unknown; type?: unknown; role?: unknown; content?: unknown } | undefined;
  if (i?.type !== "message" || i.role !== "user" || typeof i.id !== "string") return null;
  const content = Array.isArray(i.content) ? (i.content as Array<{ type?: unknown }>) : [];
  return content.some((part) => part?.type === "input_audio") ? i.id : null;
}
